import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

// Synchronize one-based URL page numbers with zero-based component state.
export default function usePageParam(pageCount, { onChange } = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [pageDraft, setPageDraft] = useState(null)
  const [navigationKey, setNavigationKey] = useState(0)

  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)
  const pageIndex = Math.min(Math.max(Number.isFinite(pageParam) ? pageParam - 1 : 0, 0), Math.max(pageCount - 1, 0))

  const goToPage = useCallback(
    n => {
      const target = Math.min(Math.max(n, 1), Math.max(pageCount, 1))
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set('page', String(target))
          return next
        },
        { replace: true }
      )
      onChange?.(target)
      setNavigationKey(key => key + 1)
    },
    [pageCount, setSearchParams, onChange]
  )

  const commitPageDraft = useCallback(() => {
    if (pageDraft !== null) goToPage(parseInt(pageDraft, 10) || pageIndex + 1)
    setPageDraft(null)
  }, [pageDraft, goToPage, pageIndex])

  const setVisiblePage = useCallback(
    index => {
      const target = Math.min(Math.max(index + 1, 1), Math.max(pageCount, 1))
      if (target === pageIndex + 1) return
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set('page', String(target))
          return next
        },
        { replace: true }
      )
      onChange?.(target)
    },
    [pageCount, pageIndex, setSearchParams, onChange]
  )

  return { pageIndex, pageDraft, navigationKey, setPageDraft, commitPageDraft, goToPage, setVisiblePage }
}
